import React, { useState } from 'react';
import SelectionScreen from './SelectionScreen';
import NumberNamingQuestionScreen from './NumberNamingQuestionScreen';
import SubitizingQuestionScreen from './SubitizingQuestionScreen';
import ComparisonQuestionScreen from './ComparisonQuestionScreen';
import PartWholeQuestionScreen from './PartWholeQuestionScreen';
import AdditionWithin5QuestionScreen from './AdditionWithin5QuestionScreen';
import KResultScreen from './KResultScreen';
import { Screen, PreKSettings as KSettings, DiagnosticAssessmentResult } from '../../types/kAssessmentTypes';
import { useAuth } from '../../context/AuthContext';
import UserProfileDropdown from '../Dashboard/UserProfileDropdown';

// Convert assessment type to track ID for JourneyPath
const assessmentTypeToTrackId = (assessmentType: 'numbernaming' | 'subitizing' | 'comparing' | 'partwhole' | 'additionwithin5'): string => {
  switch (assessmentType) {
    case 'subitizing': return 'TRACK_K_SUBITIZING';
    case 'comparing': return 'TRACK_K_COMPARING';
    case 'partwhole': return 'TRACK_K_PARTWHOLE';
    case 'additionwithin5': return 'TRACK_K_ADDITION_WITHIN_5';
    default: return 'TRACK_K_NAMING';
  }
};

// Convert focusTrack to assessment type for initial state
const focusTrackToAssessmentType = (focusTrack?: string): 'numbernaming' | 'subitizing' | 'comparing' | 'partwhole' | 'additionwithin5' => {
  switch (focusTrack) {
    case 'TRACK_K_SUBITIZING': return 'subitizing';
    case 'TRACK_K_COMPARING': return 'comparing';
    case 'TRACK_K_PARTWHOLE': return 'partwhole';
    case 'TRACK_K_ADDITION_WITHIN_5': return 'additionwithin5';
    default: return 'numbernaming';
  }
};

const KAssessment: React.FC = () => {
  const { user } = useAuth();
  const [currentScreen, setCurrentScreen] = useState<Screen>('selection');
  const [settings, setSettings] = useState<KSettings | null>(null);
  const [results, setResults] = useState<DiagnosticAssessmentResult | null>(null);
  const [currentAssessmentType, setCurrentAssessmentType] = useState<'numbernaming' | 'subitizing' | 'comparing' | 'partwhole' | 'additionwithin5'>(focusTrackToAssessmentType(user?.focusTrack));

  const handleStart = (newSettings: KSettings) => {
    setSettings(newSettings);

    // Route to appropriate assessment based on user's focus track
    if (user?.focusTrack === 'TRACK_K_SUBITIZING') {
      setCurrentScreen('subitizing');
      setCurrentAssessmentType('subitizing');
    } else if (user?.focusTrack === 'TRACK_K_COMPARING') {
      setCurrentScreen('comparing');
      setCurrentAssessmentType('comparing');
    } else if (user?.focusTrack === 'TRACK_K_PARTWHOLE') {
      setCurrentScreen('partwhole');
      setCurrentAssessmentType('partwhole');
    } else if (user?.focusTrack === 'TRACK_K_ADDITION_WITHIN_5') {
      setCurrentScreen('additionwithin5');
      setCurrentAssessmentType('additionwithin5');
    } else {
      // For TRACK_K_NAMING or no focus track, start with number naming (original sequence)
      setCurrentScreen('question');
      setCurrentAssessmentType('numbernaming');
    }
  };

  const handleNumberNamingComplete = (assessmentResults: DiagnosticAssessmentResult) => {
    setResults(assessmentResults);
    setCurrentAssessmentType('numbernaming');
    setCurrentScreen('result');
  };

  const handleSubitizingComplete = (assessmentResults: DiagnosticAssessmentResult) => {
    setResults(assessmentResults);
    setCurrentAssessmentType('subitizing');
    setCurrentScreen('result');
  };


  const handleComparingComplete = (assessmentResults: DiagnosticAssessmentResult) => {
    setResults(assessmentResults);
    setCurrentAssessmentType('comparing');
    setCurrentScreen('result');
  };

  const handlePartWholeComplete = (assessmentResults: DiagnosticAssessmentResult) => {
    setResults(assessmentResults);
    setCurrentAssessmentType('partwhole');
    setCurrentScreen('result');
  };

  const handleAdditionWithin5Complete = (assessmentResults: DiagnosticAssessmentResult) => {
    setResults(assessmentResults);
    setCurrentAssessmentType('additionwithin5');
    setCurrentScreen('result');
  };

  const handleRestart = () => {
    setSettings(null);
    setResults(null);
    setCurrentScreen('selection');
    setCurrentAssessmentType('numbernaming');
  };

  const handleContinueFromResult = () => {
    if (currentAssessmentType === 'numbernaming') {
      // After number naming, go to subitizing
      setCurrentAssessmentType('subitizing');
      setCurrentScreen('subitizing');
    } else if (currentAssessmentType === 'subitizing') {
      // After subitizing, go to comparing
      setCurrentAssessmentType('comparing');
      setCurrentScreen('comparing');
    } else if (currentAssessmentType === 'comparing') {
      // After comparing, go to part whole
      setCurrentAssessmentType('partwhole');
      setCurrentScreen('partwhole');
    } else if (currentAssessmentType === 'partwhole') {
      // After part whole, go to addition within 5
      setCurrentAssessmentType('additionwithin5');
      setCurrentScreen('additionwithin5');
    } else if (currentAssessmentType === 'additionwithin5') {
      // After addition within 5, we're done - navigate to dashboard
      window.location.replace('/');
    } else {
      // Fallback
      setCurrentScreen('selection');
    }
  };

  return (
    <div className="relative">
      {user?.role === 'admin' && (
        <div className="absolute top-4 right-4 z-50">
          <UserProfileDropdown />
        </div>
      )}
      {currentScreen === 'selection' && (
        <SelectionScreen onStart={handleStart} currentTrack={assessmentTypeToTrackId(currentAssessmentType)} />
      )}
      {currentScreen === 'question' && settings && (
        <NumberNamingQuestionScreen
          settings={settings}
          onComplete={handleNumberNamingComplete}
        />
      )}
      {currentScreen === 'subitizing' && settings && (
        <SubitizingQuestionScreen
          settings={settings}
          onComplete={handleSubitizingComplete}
        />
      )}
      {currentScreen === 'comparing' && settings && (
        <ComparisonQuestionScreen
          settings={settings}
          onComplete={handleComparingComplete}
        />
      )}
      {currentScreen === 'partwhole' && settings && (
        <PartWholeQuestionScreen
          settings={settings}
          onComplete={handlePartWholeComplete}
        />
      )}
      {currentScreen === 'additionwithin5' && settings && (
        <AdditionWithin5QuestionScreen
          settings={settings}
          onComplete={handleAdditionWithin5Complete}
        />
      )}
      {currentScreen === 'result' && results && (
        <KResultScreen
          results={results}
          assessmentType={currentAssessmentType}
          onContinue={handleContinueFromResult}
        />
      )}
    </div>
  );
};

export default KAssessment;